import { Ear, EarOff } from 'lucide-react';
import { useWakeBlock, useWakeEngine, useWakeMode, useWakeState, type WakeState } from '@/services/voice/wake/store';
import { MicLevelGraph } from './MicLevelGraph';

const TEXTO: Record<WakeState, string> = {
  parado: 'Escuta "Jarvis"/"Atlas": só na ficha do paciente e na agenda',
  verificando: 'Escuta "Jarvis": verificando…',
  ouvindo: 'Escutando "Jarvis" e "Atlas" neste aparelho (nada sai daqui até a frase). As barrinhas mostram o que o microfone capta agora',
  pausado: 'Escuta "Jarvis" pausada enquanto o ditado ouve',
  instalar: 'Escuta "Jarvis": falta instalar o português local (Configurações → Ditado por voz)',
  'sem-suporte': 'Escuta "Jarvis": este Chrome não tem reconhecimento local; não escuta',
  erro: 'Escuta "Jarvis" parou',
};

const BLOQUEIO = 'Escuta "Jarvis" DESLIGADA por segurança: o Chrome tentou usar a internet. Religue em Configurações → Ditado por voz';

/**
 * Aviso da escuta sob o botão "Ditar" (spec 011). O ponto do Chrome na aba
 * diz "microfone ligado", não "o quê" — este chip é o indicador que vale.
 * Some com o modo desligado. Ouvindo, o gráfico de nível ao lado do ouvido
 * mostra que o microfone está captando.
 */
export const WakeStatusChip = ({
  isDesktopCollapsed = false,
  flutuante = false,
}: {
  isDesktopCollapsed?: boolean;
  /** Celular (spec 024): pílula na pilha de botões flutuantes, só onde a escuta vale. */
  flutuante?: boolean;
}) => {
  const modo = useWakeMode();
  const motor = useWakeEngine();
  const { estado, erro } = useWakeState();
  const bloqueio = useWakeBlock();
  if (modo === 'off') return null;
  const ouvindo = estado === 'ouvindo' && !bloqueio;
  const problema = !!bloqueio || estado === 'instalar' || estado === 'sem-suporte' || estado === 'erro';
  const base = bloqueio ? BLOQUEIO : erro ? `${TEXTO[estado]} (${erro})` : TEXTO[estado];
  const title = motor === 'proprio' ? `${base} · motor próprio` : base;
  if (flutuante) {
    if (estado === 'parado' && !bloqueio) return null;
    return (
      <div
        role="status"
        title={title}
        aria-label={title}
        className={`md:hidden flex h-8 w-fit items-center gap-1.5 rounded-full bg-surface-container-high px-2.5 shadow-lg ${
          problema ? 'text-warning' : ouvindo ? 'text-primary' : 'text-on-surface-variant'
        }`}
      >
        {ouvindo ? <Ear size={14} className="shrink-0" aria-hidden /> : <EarOff size={14} className="shrink-0" aria-hidden />}
        {ouvindo ? (
          <MicLevelGraph ativo />
        ) : (
          <span className="text-[11px] font-medium">
            {estado === 'pausado' ? 'pausado' : estado === 'verificando' ? 'carregando…' : 'parou'}
          </span>
        )}
      </div>
    );
  }
  return (
    <div
      role="status"
      title={title}
      className={`mx-1 -mt-1 mb-2 hidden md:flex items-center gap-1.5 rounded-md px-3 py-1 text-[11px] font-medium ${
        problema ? 'text-warning' : ouvindo ? 'text-primary' : 'text-on-surface-variant/70'
      } ${isDesktopCollapsed ? 'md:justify-center md:px-0' : ''}`}
    >
      {ouvindo ? (
        <Ear size={12} className="shrink-0" aria-hidden />
      ) : (
        <EarOff size={12} className="shrink-0" aria-hidden />
      )}
      <MicLevelGraph ativo={ouvindo} />
      <span className={`truncate ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
        {ouvindo
          ? 'Jarvis · ligado'
          : estado === 'pausado'
            ? 'Jarvis · pausado'
            : problema
              ? 'Jarvis · ver configuração'
              : 'Jarvis · parado'}
      </span>
    </div>
  );
};
